import {Component, OnDestroy, OnInit} from '@angular/core';

import {Subscription, tap} from 'rxjs';

import {getSiteInfo, SiteInfo} from "@shared/helpers/site-info";
import {ShiftContextService} from "../../services/shift-context.service";
import { Site } from '@shared/models/site';
import { NewsAndNotesConfig, SafetyLevelConfig, SiteConfig } from '@shared/models/site-config';
import { FormControl } from '@angular/forms';
import { SelectItem } from 'primeng/api';
import { SitesService } from '@shared/services/sites.service';
import { MessageService } from '@shared/services/message-service';

@Component({
  selector: 'app-quality-control-config',
  templateUrl: './quality-control-config.component.html',
  styleUrls: ['./quality-control-config.component.scss']
})
export class QualityControlConfigComponent implements OnInit, OnDestroy {
    siteInfo: SiteInfo;
    siteId: Site['id'] | null = null;
    loading: boolean = true;
    error: string | null = null;
    private subscriptions: Subscription[] = [];
    private configs: SiteConfig[] = [];
    newsAndNotesUpdating: boolean = false;
    newsAndNotesControl = new FormControl<string>('');

    safetyLevelControl = new FormControl<SafetyLevelConfig>('unknown');
    safetyLevelOptions: SelectItem<SafetyLevelConfig>[] = [
        { label: 'Unknown', value: 'unknown' },
        { label: 'Low', value: 'low' },
        { label: 'Medium', value: 'medium' },
        { label: 'High', value: 'high' }
    ];

    constructor(private shiftContextService: ShiftContextService, private siteService: SitesService, private messageService: MessageService) {}

    ngOnInit() {
        this.subscriptions = [
            this.shiftContextService.CurrentContext.subscribe(context => {
                this.loading = true;
                this.error = null;
                this.siteInfo = getSiteInfo(context.site);
                this.siteId = context.site.id;

                if(!context.site.hasQualityControlEnabled) {
                    this.error = 'Quality control is not enabled for this site.';
                    this.loading = false;
                    
                    return;
                }

                this.siteService.GetSiteConfigs(this.siteId).subscribe(configs => {
                    this.setFormValues(configs);
                    this.configs = configs;
                    this.loading = false;
                })
            }),
        ];
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    private setFormValues(configs: SiteConfig[]): void {
        const newsAndNotesConfig = configs
            .find(c => c.configKey === SiteConfig.NEWS_AND_NOTES)
            ?.GetValue<NewsAndNotesConfig>();
        const safetyLevelConfig = configs
            .find(c => c.configKey === SiteConfig.SAFETY_LEVEL)
            ?.GetValue<SafetyLevelConfig>();

        this.newsAndNotesControl.setValue(newsAndNotesConfig?.notes ?? '', { onlySelf: true, emitEvent: false });
        this.safetyLevelControl.setValue(safetyLevelConfig ?? 'unknown', { onlySelf: true, emitEvent: false });
    }

    updateNewsAndNotes(): void {
        this.newsAndNotesUpdating = true;

        const val = {
            notes: this.newsAndNotesControl.value ?? '',
            timestamp: new Date().toISOString()
        };

        this.siteService.SetSiteConfig(this.siteId, SiteConfig.NEWS_AND_NOTES, JSON.stringify(val)).pipe(tap(() => {
            this.messageService.add({
                severity: 'success',
                summary: 'News and notes updated',
            });
        })).subscribe({
            error: _err => {
                this.setFormValues(this.configs);
            },
            complete: () => {
                this.newsAndNotesUpdating = false;
            }
        });
    }

    updateSafetyLevel(): void {
        const val = this.safetyLevelControl.value ?? 'unknown';

        this.siteService.SetSiteConfig(this.siteId, SiteConfig.SAFETY_LEVEL, JSON.stringify(val)).pipe(tap(() => {
            this.messageService.add({
                severity: 'success',
                summary: 'Safety level updated',
            });
        })).subscribe({
            error: _err => {
                this.setFormValues(this.configs);
            }
        });
    }
}
